<script setup>
  import { resolveCareerSpontaneousApplications } from '~/utils/career-spontaneous-applications'
  import { getEmployerLogo } from '~/utils/employer'

  definePageMeta({ layout: false })

  const { mobility: mob } = useCareerSite()
  const { params, query } = useRoute()
  const { findOne, find } = useStrapi4()
  const config = useRuntimeConfig()

  const mobility = mob.value || 'mobility' in query
  const cacheKey = `career-${params.slug}-embed${mobility ? '-mobility' : ''}`

  const careerSite = reactive({
    ...(await useAsyncData(
      cacheKey,
      async () => {
        const res = await findOne('career-sites', params.slug, {
          fields: ['slug', 'primaryColor', 'textColor', 'spontaneousApplicationTitle', 'spontaneousTypes'],
          populate: {
            employer: {
              fields: ['name', 'slug'],
              populate: {
                logo: { fields: ['url'] }
              }
            },
            logo: { fields: ['url'] }
          }
        })
        return res.data
      },
      {
        getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key] || nuxtApp.static.data[key]
      }
    ))
  })

  const careerData = computed(() => careerSite.data ?? {})
  const employer = computed(() => careerData.value.employer)

  const jobs = reactive({
    ...(await useAsyncData(
      `${cacheKey}-jobs`,
      async () => {
        if (!employer.value?.id) return []
        const res = await find('jobs', {
          fields: ['slug', 'title'],
          filters: {
            careerDisplay: !mobility ? true : undefined,
            employer: employer.value.id,
            mobilityDisplay: mobility ? true : undefined,
            published: true
          },
          pagination: { limit: 6, start: 0 },
          populate: {
            locations: { fields: ['city'] }
          },
          sort: { validatedAt: 'desc' }
        })
        return res.data
      },
      {
        getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key] || nuxtApp.static.data[key]
      }
    ))
  })

  const baseUrl = computed(() => {
    const domain = mobility ? config.public.mobilityDomain : config.public.careerDomain
    return domain ? `https://${domain}/${careerData.value.slug}` : `${config.public.baseUrl}/career/${careerData.value.slug}`
  })

  const spontaneousSettings = computed(() => resolveCareerSpontaneousApplications(careerData.value))
  const enabledSpontaneousTypes = computed(() => spontaneousSettings.value.spontaneousTypes.filter(type => type.enabled))
  const spontaneousApplicationUrl = spontaneousType => `${baseUrl.value}?spontanee=${spontaneousType}`

  const employerLogo = computed(() => getEmployerLogo(employer.value))

  if (employerLogo.value?.url) {
    useFavicon(useStrapiMedia(employerLogo.value.url) || '/images/favicon.svg')
  }

  useHead({
    title: `${employer.value?.name || ''} - ${mobility ? 'Mobilité interne' : "Offres d'emploi"}`,
    bodyAttrs: {
      class: 'font-sans text-base bg-transparent'
    }
  })
</script>

<template>
  <div class="min-h-screen bg-transparent p-4" :style="{ color: careerData.textColor }">
    <Error v-if="careerSite.status === 'error' || jobs.status === 'error'" :retry="careerSite.refresh" />

    <div class="space-y-6" v-else>
      <h1 class="text-center text-xl font-bold">Nos offres d'emploi</h1>

      <template v-if="jobs.data?.length">
        <div class="grid grid-cols-1 gap-x-10 gap-y-4 md:grid-cols-2" v-if="jobs.data?.length">
          <PagesCareerJobsCard
            class="!mx-0 !max-w-none !px-6 !py-4 !shadow-md hover:!shadow-lg"
            v-for="job in jobs.data"
            :key="job.id"
            :color="careerData.primaryColor"
            :job="job"
            :external="true"
            :to="`${baseUrl}/jobs/${job.slug}`"
          />
        </div>

        <div class="text-center" v-if="jobs.data?.length">
          <NuxtLink :href="`${baseUrl}/jobs`" external target="_blank">
            <Button
              class="!border-transparent !bg-white !font-sans !text-base !shadow-md !ring-0 hover:!shadow-lg"
              color="primary"
              outline
              size="lg"
              :style="{ color: careerData.textColor }"
            >
              Voir toutes les offres
            </Button>
          </NuxtLink>
        </div>
      </template>
      <p class="text-center" v-else>Aucune offre disponible actuellement</p>

      <section class="flex flex-col items-center gap-4 pt-2 text-center" v-if="!mobility && enabledSpontaneousTypes.length">
        <h2 class="text-xl font-bold">{{ spontaneousSettings.title }}</h2>
        <div class="flex flex-wrap justify-center gap-4">
          <NuxtLink
            v-for="type in enabledSpontaneousTypes"
            :key="type.spontaneousType"
            :href="spontaneousApplicationUrl(type.spontaneousType)"
            external
            target="_blank"
          >
            <Button
              class="!border-2 !bg-white !font-sans !text-lg no-underline !shadow-md hover:!text-[var(--primary-color)] hover:!shadow-lg"
              color="link"
              outline
              :style="{ borderColor: careerData.primaryColor, '--primary-color': careerData.primaryColor }"
            >
              {{ type.label }}
            </Button>
          </NuxtLink>
        </div>
      </section>
    </div>
  </div>
</template>
